import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { mapArrayToObject } from "../../../commonFunctions/arrayFunc";
import { useFormContext } from "../../../context/FormContext";
import { useTableContext } from "../../../context/TableContext";
import BuilderModal from "../../../Layouts/Modal/BuilderModal";
import Input from "../../FormComponents/Input";
import SingleSelectBox from "../../FormComponents/SingleSelectBox";
import ButtonActionIcon from "../ButtonActionIcon";
import FieldSelectorFromPopup from "../FieldSelectorFromPopup";
import TableTreeModal from "../TableTreeModal";
import MaterialIcon from "../MaterialIcon";
import { FromTokenDropdownWithFetch } from "../FromTokenDropdown";
import { isValidObjectId } from "../../../utils/objectIdUtils";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { toastError } from "../../../utils/toastUtils";

const ValueAccordingOperator = ({
  value,
  // index,
  updatePrefilter,
  field,
  tableId,
  tableObj: initialTableObj,
  filterValues
}) => {
  const { selectedForm } = useFormContext();
  const { getTableTypeData } = useTableContext();
  const { smId } = useParams();
  const [rdbmsTemplates, setRdbmsTemplates] = useState([]);

  const [tableObj, setTableObj] = useState(initialTableObj);

  const fetchRdbms = async () => {
    try {
      const { data } = await axiosInstance.get(`/rdbms`);
      const groupRdbms = data.filter((rdbms) => rdbms.is_group);
      setRdbmsTemplates(groupRdbms);
    } catch (error) {
      handleApiError(error);
    }
  };

  const updateRdbmsGroup = (objKey, type, value) => {
    const copyField = field[type] ? { ...field[type] } : { template_id: "", text_value: "" };

    copyField[objKey] = value;

    updatePrefilter(type, copyField);
  };

  useEffect(() => {
    const fetchTableObject = async (id) => {
      try {
        const { tableObject } = await getTableTypeData("statemachine", id);
        setTableObj({ ...tableObject, ...initialTableObj });
      } catch (error) {
        console.error("Error fetching table object:", error);
        setTableObj({ ...initialTableObj });
      }
    };
    if (smId) {
      fetchTableObject(smId);
    } else {
      setTableObj({ ...initialTableObj });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTableTypeData, selectedForm.parentData, initialTableObj]);

  useEffect(() => {
    fetchRdbms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  switch (value) {
    case "number_value":
      return (
        //0 is not showing as false value so undefined condition
        <Input
          type="number"
          value={field[value] !== undefined ? field[value] : ""}
          onChange={(e) => updatePrefilter(value, e.target.value)}
        />
      );
    case "text_value":
      if (field.operator === "in_http_output" || field.operator === "eq_http_output") {
        return <HttpOutputFilterInput field={field} updatePrefilter={updatePrefilter} />;
      }
      const fromTokenOperator = ["from_token", "contains_from_token", "a_contains_from_token"];
      if (fromTokenOperator.includes(field.operator)) {
        return (
          <>
            {!isValidObjectId(field[value] || "") && <label>{field[value] || ""}</label>}
            <FromTokenDropdownWithFetch
              selected={field[value] || ""}
              onSelect={(data) => updatePrefilter(value, data.id)}
            />
          </>
        );
      }
      return <Input type="text" value={field[value] || ""} onChange={(e) => updatePrefilter(value, e.target.value)} />;
    case "rdbms_value":
      return (
        <>
          <SingleSelectBox
            selectedData={field[value]?.template_id || ""}
            options={rdbmsTemplates}
            onChange={(obj) => updateRdbmsGroup("template_id", value, obj.id)}
          />
          <Input
            type="text"
            value={field[value]?.text_value || ""}
            onChange={(e) => updateRdbmsGroup("text_value", value, e.target.value)}
          />
        </>
      );

    case "text_array":
      if (field.id.includes("group_details")) {
        return (
          <GroupIDsArrayChips value={field[value] || []} callbackFunction={(data) => updatePrefilter(value, data)} />
        );
      }
      return <TextArrayChips value={field[value] || []} callbackFunction={(data) => updatePrefilter(value, data)} />;
    case "number_range":
    case "custom_date_range":
      return (
        <NumberRange
          type={value}
          value={field[value] || []}
          callbackFunction={(data) => updatePrefilter(value, data)}
        />
      );
    case "human_date":
      return <HumanDate value={field[value] || ""} callbackFunction={(data) => updatePrefilter(value, data)} />;
    case "logged_in_extra":
    case "logged_in_user":
      return (
        <AppUserAllFields
          value={field[value] || ""}
          callbackFunction={(data) => updatePrefilter(value, data)}
          tableId={tableId}
          // folderId={["logged_in_user"]}
          tableObj={tableObj}
          tableType="appuser"
        />
      );
    case "instance_value":
      if (filterValues) {
        return (
          <FilterValueSelection
            selected={field[value] || {}}
            filterValues={filterValues}
            onChange={(data) => updatePrefilter(value, data)}
          />
        );
      }
      let newTableId = tableId;
      if (smId) newTableId = smId;

      return (
        <AppUserAllFields
          value={field[value] || ""}
          callbackFunction={(data) => updatePrefilter(value, data)}
          tableId={newTableId}
          tableObj={tableObj}
          tableType="statemachine"
        />
      );

    default:
      return <h3 className="builder_default_text">no action</h3>;
  }
};
const FilterValueSelection = ({ selected, filterValues, onChange }) => {
  const handleChangeFilterValues = (data) => {
    onChange(data);
  };
  const addLookupField = (data) => {
    onChange({ ...selected, target_instance_value_field: data });
  };

  // const isLookup = selected.field_type === "lookup" || selected.field_type === "lookup-sm";
  const tableId = selected.table_id || "";
  return (
    <div>
      <SingleSelectBox
        selectedData={selected || ""}
        options={filterValues}
        labelKey={["display"]}
        onChange={handleChangeFilterValues}
      />
      {/* {!tableId && <div>Table Not Found</div>} */}
      {tableId && (
        <FieldSelectorFromPopup
          tableId={tableId}
          tableType={"statemachine"}
          onSelect={addLookupField}
          fieldId={selected["target_instance_value_field"]?.id || ""}
        />
      )}
    </div>
  );
};
const NumberRange = ({ type, value, callbackFunction }) => {
  const saveRange = (position, data) => {
    const newArray = [...value];
    newArray[position] = data;
    callbackFunction(newArray);
  };
  return (
    <div className="d_flex align_center">
      <Input
        type={type === "number_range" ? "number" : "date"}
        value={value[0] || ""}
        onChange={(e) => saveRange(0, e.target.value)}
      />
      <span>To</span>
      <Input
        type={type === "number_range" ? "number" : "date"}
        value={value[1] || ""}
        onChange={(e) => saveRange(1, e.target.value)}
      />
    </div>
  );
};

const TextArrayChips = ({ value, callbackFunction }) => {
  const [textValue, setTextValue] = useState("");

  const addText = () => {
    if (textValue.trim().length === 0) {
      toastError("At least one character required");
      return;
    }
    setTextValue("");
    callbackFunction([...value, textValue]);
  };

  const removeText = (index) => {
    const newArray = value.filter((_, i) => i !== index);
    callbackFunction(newArray);
  };

  return (
    <div className="flex_column">
      {/* <ButtonActionIcon type="edit" size="xsm" onClick={addText} /> */}
      <div className="d_flex w_100 g_8">
        <Input type="text" value={textValue} onChange={(e) => setTextValue(e.target.value)} />
        <ButtonActionIcon type="add" size="md" onClick={addText} />
      </div>
      <div className="selected_chip_container mt_3">
        {value.map((name, index) => (
          <div className="selected_chip_items" key={name}>
            {name} <MaterialIcon name="close" onClick={() => removeText(index)} />
          </div>
        ))}
      </div>
    </div>
  );
};

const HumanDate = ({ value, callbackFunction }) => {
  const dateOptions = [
    { id: "today", name: "Today" },
    { id: "yesterday", name: "Yesterday" },
    { id: "tomorrow", name: "Tomorrow" },
    { id: "current_week", name: "Current Week" },
    { id: "last_week", name: "Last Week" },
    { id: "next_week", name: "Next Week" },
    { id: "current_month", name: "Current Month" },
    { id: "last_month", name: "Last Month" },
    { id: "next_month", name: "Next Month" },
    { id: "current_year", name: "Current Year" },
    { id: "last_year", name: "Last Year" },
    { id: "next_year", name: "Next Year" },
    { id: "after_today_exclusive", name: "After today exclusive" },
    { id: "before_today_exclusive", name: "Before today exclusive" },
    { id: "after_today_inclusive", name: "After today inclusive" },
    { id: "before_today_inclusive", name: "Before today inclusive" },
    { id: "not_today", name: "Not Today" },
    { id: "not_yesterday", name: "Not Yesterday" },
    { id: "not_tomorrow", name: "Not Tomorrow" },
    { id: "not_current_week", name: "Not Current Week" },
    { id: "not_last_week", name: "Not Last Week" },
    { id: "not_next_week", name: "Not Next Week" },
    { id: "not_current_month", name: "Not Current Month" },
    { id: "not_last_month", name: "Not Last Month" },
    { id: "not_next_month", name: "Not Next Month" },
    { id: "not_current_year", name: "Not Current Year" },
    { id: "not_last_year", name: "Not Last Year" },
    { id: "not_next_year", name: "Not Next Year" }
  ];

  return (
    <SingleSelectBox
      selectedData={value}
      onChange={(obj) => callbackFunction(obj.id)}
      options={dateOptions}
      labelKey={["name"]}
      placeholder="Select date"
    />
  );
};

const AppUserAllFields = ({ value, callbackFunction, tableId, tableObj, tableType }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleAppuserField = (data) => {
    callbackFunction(data.id);
    setIsModalOpen(false);
  };
  const { getTableTypeData } = useTableContext();
  const [appuserTableObj, setAppUserTableObject] = useState({});
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { tableObject } = await getTableTypeData(tableType, tableId);
        setAppUserTableObject({ ...tableObject, ...tableObj });
      } catch (error) {
        handleApiError(error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="builder_dropdown_outer">
        <div className="builder_input" onClick={() => setIsModalOpen(true)}>
          {value === "" ? "Select field" : `${appuserTableObj[value]?.prefix}.${appuserTableObj[value]?.display}`}
        </div>
      </div>
      <BuilderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} modalName="Select Appuser Field">
        <TableTreeModal
          callbackFunction={handleAppuserField}
          selectedFile={appuserTableObj && appuserTableObj[value]?.id}
          tableType={tableType}
          tableId={tableId}
        />
      </BuilderModal>
    </>
  );
};

const GroupIDsArrayChips = ({ value, callbackFunction }) => {
  const { orgId } = useParams();
  const [listOfGrops, setListOfGroups] = useState({});
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState("");

  const fetchAllApplicationGroups = async () => {
    try {
      const { data } = await axiosInstance.get(`/user-service/groups/org/${orgId}`);
      setGroups(data);
      const { result } = mapArrayToObject(data);
      setListOfGroups(result);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleChangeGroup = (groupValue) => {
    setSelectedGroups(groupValue);
    if (!groupValue) return;
    callbackFunction([...value, groupValue]);
  };

  const removeGroup = (index) => {
    const newArray = value.filter((_, i) => i !== index);
    callbackFunction(newArray);
  };
  useEffect(() => {
    fetchAllApplicationGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <SingleSelectBox
        selectedData={selectedGroups}
        options={groups}
        onChange={(obj) => handleChangeGroup(obj.id)}
        placeholder="Select group"
      />
      {Object.keys(listOfGrops).length > 0 &&
        value.map((key, index) => (
          <div className="selected_chip_items" key={key}>
            {listOfGrops[key].name || ""}
            <MaterialIcon name="close" onClick={() => removeGroup(index)} />
          </div>
        ))}
    </div>
  );
};

const HttpOutputFilterInput = ({ field, updatePrefilter }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.get(`/http-request-templates`);
        const list = Array.isArray(data) ? data : data.data || [];
        setTemplates(list);
      } catch (error) {
        console.error("Failed to fetch HTTP templates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  return (
    <div>
      <div>
        <select
          className="p_1 border_full border_radius_small"
          value={field.text_value || ""}
          disabled={loading}
          onChange={(e) => updatePrefilter("text_value", e.target.value)}
        >
          <option value="" disabled>
            {loading ? "Loading templates..." : "-- Select HTTP Template --"}
          </option>
          {templates.map((tmpl) => (
            <option key={tmpl.id} value={tmpl.id}>
              {tmpl.name || tmpl.id}
            </option>
          ))}
        </select>
        <span style={{ fontSize: "10px" }}>"email" and "upn" is going inside payload</span>
      </div>

      {/* 2. Path Input */}
      <div className="d_flex flex_column mt_1">
        <Input
          type="text"
          placeholder="e.g. $.users[*].email"
          value={field.http_output_path || ""}
          onChange={(e) => updatePrefilter("http_output_path", e.target.value)}
        />
        <span style={{ fontSize: "10px" }}>This is a Use to select the value from the API response.</span>
      </div>
    </div>
  );
};

export default ValueAccordingOperator;
